import { useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Radio,
  RadioGroup,
  Stack,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';

export interface DeliveryResendModalData {
  /** Email resends note that BCCs are re-sent too. */
  isEmail: boolean;
  /** Channel short name for the title (e.g. "email"). */
  shortName: string;
  hasReadOnlyPermission: boolean;
  /** Fires the resend with the chosen tracking preference. */
  onConfirm: (tracked: boolean) => void;
}

/**
 * Resend confirmation with a tracking-preference choice. The message-limit
 * override radio is deferred — there is no message-limit resource in the data
 * layer yet, so this collects only the `tracked` flag the redeliver endpoint
 * accepts.
 */
export function DeliveryResendModal({ data, onClose, modalState }: PlumaManagedModalProps<DeliveryResendModalData>) {
  const [trackedChoice, setTrackedChoice] = useState('true');

  const handleResend = () => {
    data.onConfirm(trackedChoice === 'true');
    onClose();
  };

  return (
    <Modal title={`Resend ${data.shortName}`} state={modalState} data-test-delivery-redeliver-modal>
      <ModalBody>
        <Stack gap="200">
          <Paragraph data-test-delivery-redeliver-modal-headsup>
            You’re about to send this message again. All recipients
            {data.isEmail ? ' (including BCCs)' : ''} will be sent the exact same content they were sent the last time.
          </Paragraph>
          <RadioGroup
            label="Do you want to track opens and clicks?"
            name="tracked"
            value={trackedChoice}
            onChange={setTrackedChoice}
          >
            <Radio
              id="tracked_yes"
              label="Track opens and link clicks (recommended)"
              value="true"
              data-test-delivery-redeliver-modal-tracking-input="true"
            />
            <Radio
              id="tracked_no"
              label="Don't track opens and link clicks"
              value="false"
              data-test-delivery-redeliver-modal-tracking-input="false"
            />
          </RadioGroup>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={() => onClose()} data-test-delivery-redeliver-confirm-modal-cancel>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleResend}
          isDisabled={data.hasReadOnlyPermission}
          data-test-delivery-redeliver-confirm-modal-submit
        >
          Resend message
        </Button>
      </ModalFooter>
    </Modal>
  );
}
